'use client';

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { ReactCompareSlider, ReactCompareSliderImage } from 'react-compare-slider';
import type { Project } from '@portfolio/types';

interface ProjectShowcaseProps {
  project: Project;
}

export default function ProjectShowcase({ project }: ProjectShowcaseProps) {
  const {
    title,
    tagline,
    description,
    imageUrl,
    imageAlt,
    liveUrl,
    caseStudyUrl,
    downloadUrl,
    techStack,
    layout,
  } = project;

  const imageComponent =
    project.slug === 'curb-appeal' ? (
      <div className="flex flex-col gap-4 md:col-span-3">
        <motion.div
          className="w-full rounded-lg shadow-2xl overflow-hidden border-2 border-border-color/50"
          whileHover={{ scale: 1.02 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          <div className="relative aspect-[16/9] w-full">
            <ReactCompareSlider
              itemOne={<ReactCompareSliderImage src="/images/before-2.jpeg" alt="Before staging" />}
              itemTwo={<ReactCompareSliderImage src="/images/after-2.jpg" alt="After staging" />}
            />
          </div>
        </motion.div>
        <div className="grid grid-cols-2 gap-4">
          <div className="w-full rounded-md shadow-lg overflow-hidden border-2 border-border-color/50">
            <Image src="/images/curbappeal homepage.png" alt="Curb Appeal Homepage" width={800} height={450} className="object-cover" />
          </div>
          <div className="w-full rounded-md shadow-lg overflow-hidden border-2 border-border-color/50">
            <Image src="/images/after-1.jpg" alt="Curb Appeal Example" width={800} height={450} className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    ) : (
      <motion.div
        className="md:col-span-3"
        whileHover={{ scale: 1.03 }}
        transition={{ duration: 0.3 }}
      >
        <div className="relative aspect-[16/10] rounded-lg shadow-2xl border-2 border-border-color/50 overflow-hidden">
          <Image
            src={imageUrl}
            alt={imageAlt}
            width={1200}
            height={600}
            className={`w-full h-full ${project.slug === 'leadflow-ai' || project.slug === 'smartsoil-estimator' || project.slug === 'vantage' ? 'object-contain' : 'object-cover'}`}
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        </div>
      </motion.div>
    );

  const textComponent = (
    <div className="md:col-span-2">
      <h3 className="text-3xl lg:text-4xl font-bold text-white">{title}</h3>
      <p className="mt-2 text-lg text-brand-accent font-semibold">{tagline}</p>
      <p className="mt-6 text-base text-text-primary leading-relaxed space-y-4">
        {description}
      </p>
      <div className="flex flex-wrap gap-2 mt-8">
        {techStack.map((tech) => (
          <span
            key={tech}
            className="px-3 py-1 bg-surface-100 border border-border-color rounded-full text-sm text-text-secondary"
          >
            {tech}
          </span>
        ))}
      </div>
      <div className="mt-8">
        {liveUrl && (
          <Link
            href={liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 font-semibold text-white bg-brand-accent rounded-md hover:bg-brand-accent-hover transition-colors"
          >
            View Live Project →
          </Link>
        )}
        {caseStudyUrl && (
          <Link
            href={caseStudyUrl}
            className="inline-flex items-center gap-2 ml-4 px-6 py-3 font-semibold text-white bg-surface-200 rounded-md hover:bg-surface-100 transition-colors"
          >
            View Deep Dive →
          </Link>
        )}
        {downloadUrl && (
          <Link
            href={downloadUrl}
            className="inline-flex items-center gap-2 ml-4 px-6 py-3 font-semibold text-white bg-brand-accent rounded-md hover:bg-brand-accent-hover transition-colors"
          >
            {project.slug === 'photo-processor' ? 'Download for macOS →' : 'Download →'}
          </Link>
        )}
      </div>
    </div>
  );

  return (
    <motion.section
      id={project.slug}
      className="w-full py-24 sm:py-32"
      initial={{ opacity: 0, y: 100 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
    >
      <div className="container mx-auto grid grid-cols-1 md:grid-cols-5 gap-12 items-center">
        {layout === 'image-left' ? (
          <>
            {imageComponent}
            {textComponent}
          </>
        ) : (
          <>
            <div className="md:order-last md:col-span-3">{imageComponent}</div>
            {textComponent}
          </>
        )}
      </div>
    </motion.section>
  );
}
